import { protectPdfOutput } from "./protectPdfOutput";
import { keepPdfBlockTogether } from "./pdfPagination";
import { jsPDF } from "jspdf";
import { loadPdfDocumentHeader } from "./PdfDocumentHeader";
import type { C1_PdfChapterArtifact } from "../../ConceptOne/src/lib/C1_PdfChapter";
import { registerC1PdfFonts } from "../../ConceptOne/src/lib/C1_PdfFonts";
import { compareTags } from "../utils/tagSort";
import type { State } from "../State";
import { collectBillOfMaterials, materialChapter, materialChapterOrder, materialSectionOrder as sectionOrder, type BillOfMaterialsOptions } from "./billOfMaterialsReport";

const margin = 45;
const quantityRight = 542;
const bottom = 780;

/** Renders the shared ESTADA material report for either application and applies licensing. */
export async function createBillOfMaterialsPdf(app: State, options: BillOfMaterialsOptions, projectName = app.projectMetadata.name): Promise<C1_PdfChapterArtifact> {
  const document = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  await registerC1PdfFonts(document);
  const header = await loadPdfDocumentHeader(app);
  const scopes = collectBillOfMaterials(app, options);
  const pages: C1_PdfChapterArtifact["pages"] = [];
  const tocEntries: C1_PdfChapterArtifact["pages"] = [];
  let pageIndex = 0;
  let firstPage = true;
  let y = 100;
  let currentScope = "";
  let currentChapter = "";
  let currentScopeIsTotal = false;
  let usageHeaderShown = false;
  let currentScopeIndex = 0;
  const continuousTotal = options.mode === "total" && !options.usage;

  const newPage = (scope: string, continuation = false) => {
    if (!firstPage) document.addPage("a4", "portrait");
    firstPage = false;
    usageHeaderShown = false;
    currentScope = scope;
    const byFloor = options.mode === "floors";
    const pageTitle = byFloor ? currentChapter : scope;
    const page = { title: `${pageTitle}${continuation ? " · Fortsetzung" : ""}`, pageIndex: pageIndex++,
      sectionTitle: byFloor ? scope : currentChapter,
      sectionId: byFloor ? `material-scope-${currentScopeIndex}` : currentChapter,
      omitNavigationChild: !byFloor && currentScopeIsTotal };
    pages.push(page);
    if (!continuation) tocEntries.push(page);
    header.draw(document, { planType: "Stückliste", hierarchy: [projectName,
      currentChapter ? (byFloor ? `${scope} · ${currentChapter}` : `${currentChapter} · ${scope}`) : scope] });
    document.setFont("helvetica", "bold");
    document.setFontSize(8);
    document.setTextColor("#586a75");
    if (!options.usage) document.text("Anzahl", quantityRight, 94, { align: "right" });
    y = 110;
    if (options.usage && !(options.mode === "floors" && currentScopeIsTotal)) drawUsageHeader();
  };
  const ensureSpace = (height: number) => {
    keepPdfBlockTogether(y, bottom, [height], () => newPage(currentScope, true));
  };
  const drawUsageHeader = () => {
    if (usageHeaderShown) return;
    document.setFillColor("#d9e0e4");
    document.rect(margin, y - 10, 550 - margin, 14, "F");
    document.setFont("helvetica", "bold");
    document.setFontSize(6.5);
    document.setTextColor("#586a75");
    const captions = ["Tag", "Etage", "Raum", "Name"];
    const offsets = [18, 110, 210, 310];
    captions.forEach((caption, index) => document.text(caption, margin + offsets[index], y));
    document.text("Stückzahl", quantityRight, y, { align: "right" });
    y += 19;
    usageHeaderShown = true;
  };

  const chapterScopes = materialChapterOrder.flatMap(chapter => scopes.flatMap((scope, scopeIndex) => {
    const positions = scope.positions.filter(position => materialChapter(position.section) === chapter);
    return positions.length ? [{ ...scope, positions, chapter, scopeIndex }] : [];
  }));
  if (options.mode === "floors") chapterScopes.sort((a, b) => a.scopeIndex - b.scopeIndex);
  chapterScopes.forEach((scope) => {
    const showUsage = options.usage && !(options.mode === "floors" && scope.isTotal);
    currentChapter = scope.chapter;
    currentScopeIsTotal = !!scope.isTotal;
    currentScopeIndex = scope.scopeIndex;
    if (!continuousTotal || firstPage) newPage(scope.title);
    else {
      currentScope = scope.title;
      ensureSpace(70);
      tocEntries.push({ title: scope.title, pageIndex: pageIndex - 1,
        sectionTitle: currentChapter, sectionId: currentChapter, omitNavigationChild: true });
    }
    if (continuousTotal) {
      document.setFont("helvetica", "bold");
      document.setFontSize(14);
      document.setTextColor("#172b3a");
      document.text(currentChapter, margin, y);
      y += 25;
    }
    const positions = scope.positions;
    const sections = [...new Set([...sectionOrder, ...positions.map(position => position.section)])];
    sections.forEach((section) => {
      const sectionPositions = positions.filter((position) => position.section === section);
      if (!sectionPositions.length) return;
      const preparedPositions = sectionPositions.map(position => {
        document.setFont("helvetica", "normal");
        document.setFontSize(8.5);
        const specificationLines = document.splitTextToSize(position.specification, 400) as string[];
        document.setFontSize(7);
        const usages = (showUsage ? [...position.usages] : []).sort((a, b) => compareTags(a.tag, b.tag, app.settings.language)).map(item => {
          const values = [item.tag, item.floor, item.room, item.name];
          const widths = [84, 92, 92, 190];
          const lines = values.map((value, index) => document.splitTextToSize(value, widths[index]) as string[]);
          return { lines, height: Math.max(...lines.map(value => value.length), 1) * 9 + 5 };
        });
        const showGroup = position.group.localeCompare(section, "de", { sensitivity: "base" }) !== 0;
        const redundantSocketSpecification = section === "Steckdosen" && (
          (position.group === "Dauerstrom" && position.specification === "230V") ||
          (position.group === "Geschaltet" && ["Schalten", "Switch"].includes(position.specification)));
        const collapseIntoGroup = showGroup && !position.productId && (
          redundantSocketSpecification ||
          (section === "Leuchten" && !options.distinguishLightTypes) ||
          position.specification.localeCompare(position.group, "de", { sensitivity: "base" }) === 0);
        const displayLines = collapseIntoGroup ? document.splitTextToSize(position.group, 400) as string[] : specificationLines;
        const displayRowHeight = Math.max(17, displayLines.length * 10 + 7);
        const openingHeight = (showGroup && !collapseIntoGroup ? 14 : 0) + displayRowHeight + (showUsage ? 19 + (usages[0]?.height ?? 0) : 0);
        return { position, specificationLines: displayLines, rowHeight: displayRowHeight, usages, showGroup, collapseIntoGroup, openingHeight };
      });
      const sectionTitle = section === "Heizung" ? "Heizen" : section === "Fensterkontakte" ? "Kontakte" : section;
      if (section !== "Schaltschrankgeräte" && (!continuousTotal || sectionTitle !== currentChapter)) {
        ensureSpace(25 + preparedPositions[0].openingHeight);
        document.setFont("helvetica", "bold");
        document.setFontSize(12);
        document.setTextColor("#172b3a");
        document.text(sectionTitle, margin, y);
        y += 25;
      }
      let previousGroup = "";
      preparedPositions.forEach(({ position, specificationLines, rowHeight, usages, showGroup, collapseIntoGroup, openingHeight }) => {
        document.setFont("helvetica", "normal");
        document.setFontSize(8.5);
        const previousPage = pageIndex;
        ensureSpace(openingHeight);
        if (showUsage && usages.length) drawUsageHeader();
        if (showGroup && !collapseIntoGroup && (position.group !== previousGroup || previousPage !== pageIndex)) {
          document.setFont("helvetica", "bold");
          document.setFontSize(8);
          document.setTextColor("#586a75");
          document.text(position.group, margin, y);
          y += 14;
          previousGroup = position.group;
        }
        {
          document.setDrawColor("#d9e0e4");
          document.line(margin, y + rowHeight - 10, 550, y + rowHeight - 10);
          document.setFont("helvetica", "normal");
          document.setFontSize(8.5);
          document.setTextColor("#172b3a");
          document.text(specificationLines, margin + 8, y);
          document.setFont("helvetica", "normal");
          document.text(String(position.count), quantityRight, y, { align: "right" });
          y += rowHeight;
        }
        if (showUsage) {
          usages.forEach(({ lines, height }) => {
            const offsets = [18, 110, 210, 310];
            // Reserve the heading again if this usage starts a continuation page.
            if (y + height + (usageHeaderShown ? 0 : 19) > bottom) newPage(currentScope, true);
            drawUsageHeader();
            document.setFont("helvetica", "normal");
            document.setFontSize(7);
            document.setTextColor("#586a75");
            for (let column = 0; column < lines.length; column++) {
              document.text(lines[column], margin + offsets[column], y);
            }
            y += height;
          });
          y += 4;
        }
      });
      y += 16;
    });
  });

  if (firstPage) {
    newPage("Gesamt");
    document.setFont("helvetica", "normal");
    document.setFontSize(9);
    document.text("Keine Materialpositionen für die gewählte Auswahl vorhanden.", margin, y);
  }

  const totalPages = document.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    document.setPage(page);
    const width = document.internal.pageSize.getWidth();
    document.setDrawColor("#aeb9bf");
    document.line(margin, 794, width - margin, 794);
    document.setFont("helvetica", "normal");
    document.setFontSize(6.5);
    document.setTextColor("#586a75");
    document.text(`Projekt: ${projectName}`, margin, 808);
    document.text(`${page} / ${totalPages}`, width - margin, 808, { align: "right" });
  }
  return { blob: await protectPdfOutput(document.output("blob"), "list"), pages, tocEntries };
}
